/**
 * /onboarding — dedicated wizard route for first-time users.
 * Premium bright design system (dashboard-premium.css).
 *
 * Two-column layout:
 *   left:  floating step-list card with progress markers + premium progress bar
 *   right: active-step glass card with title/description + primary CTA + "Skip"
 *
 * Skipping or completing the active step advances to the next pending one.
 * "Skip for now" is always visible — onboarding is non-blocking.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOnboarding } from "@/context/OnboardingContext";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import "./dashboard-premium.css";
import { CheckCircle, Circle, ArrowRight, X, SkipForward } from "@phosphor-icons/react";
import api from "@/lib/api";

// Hoisted out of `StepBody` so React sees a stable component type per render
// (avoids the react/no-unstable-nested-components warning; behaviour is
// unchanged since `P` carries no state).
const P = ({ children }) => (
  <p className="text-[14px] text-[var(--hd-text-muted)] leading-relaxed">{children}</p>
);

// Inline step bodies — each step gets a focused one-screen UX.
function StepBody({ step, onPrimary, onSkip, primaryBusy }) {
  const sid = step.id;
  if (sid === "welcome")
    return (
      <div>
        <P>
          Hello by Flow is your team's cloud phone system — make and receive calls from any browser,
          send SMS, run meetings, and watch your numbers grow in real-time.
        </P>
        <ul className="mt-5 space-y-2.5 text-[14px]">
          {[
            "Get a real phone number with your Communication Seat (or use our demo number to start)",
            "Click-to-dial from contacts, calls, or your dialer",
            "Live transcription, recording, voicemail-to-text",
            "Supervisors can listen, whisper, or barge into any agent call",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2.5">
              <CheckCircle size={17} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  if (sid === "confirm_plan")
    return <PlanRecap />;
  if (sid === "profile")
    return <ProfileEditor onSaved={onPrimary} />;
  if (sid === "billing")
    return (
      <P>
        Connect a payment method now to remove the trial banner, or skip and add it later from
        <span className="font-mono text-[13px]"> Settings → Billing</span>.
      </P>
    );
  if (sid === "first_number")
    return (
      <P>
        Pick a real Twilio number (local, toll-free, or mobile) and we'll attach it to your
        workspace. You can also skip and start with the shared demo number for testing.
      </P>
    );
  if (sid === "test_call")
    return (
      <P>
        We'll open the dialer pre-filled with our test number. Tap "Call" and confirm you can
        hear the dial tone — that proves your mic, speakers, and browser permissions are good.
      </P>
    );
  if (sid === "test_sms")
    return (
      <P>
        Send a one-line SMS to your own number from the Messages page. We'll mark this step
        done automatically once it goes through.
      </P>
    );
  if (sid === "tour")
    return (
      <P>
        Take a quick walkthrough of the dashboard — calls timeline, contact search, live
        analytics, and the global dialer on the right.
      </P>
    );
  if (sid === "invite_team")
    return (
      <P>
        Add agents and supervisors by email from the Team page. Each invite consumes one seat
        of your plan.
      </P>
    );
  if (sid === "seats_roles")
    return (
      <P>
        Decide who's an admin (full control), supervisor (oversight + coaching), or agent
        (make calls). Change roles anytime from the Team page.
      </P>
    );
  if (sid === "business_hours")
    return (
      <P>
        Tell us when your team is on the phones. Outside those hours, inbound calls go to
        voicemail with a custom message.
      </P>
    );
  if (sid === "ivr_routing")
    return (
      <P>
        Build a phone tree — press 1 for sales, 2 for support, etc. Drag-and-drop visual
        editor from the IVR page.
      </P>
    );
  if (sid === "voicemail_setup")
    return (
      <P>
        Record a voicemail greeting and pick a transcription style — short summary, full text,
        or sentiment-only.
      </P>
    );
  if (sid === "greetings")
    return (
      <P>
        Set a branded welcome message that callers hear before reaching an agent. Pulled from
        the IVR builder.
      </P>
    );
  if (sid === "call_recording")
    return (
      <P>
        Decide which calls get recorded (all / outbound / inbound / none) and how long
        recordings are kept.
      </P>
    );
  if (sid === "departments")
    return (
      <P>
        Group agents into departments (Sales / Support / Billing). Routes calls smarter and
        powers per-department analytics.
      </P>
    );
  if (sid === "compliance")
    return (
      <P>
        Configure recording-consent prompts, your Do-Not-Call list import, and data-residency
        preferences for regulated industries.
      </P>
    );
  return null;
}

function PlanRecap() {
  const { user } = useAuth();
  const org = user?.organization || {};
  return (
    <div className="space-y-3 text-[14px]">
      <Row label="Workspace" value={org.name || "—"} />
      <Row label="Plan" value={(org.plan || "—").toUpperCase()} />
      <Row label="Account type" value={org.type === "business" ? "Business (multi-seat)" : "Solo"} />
      <p className="text-[12px] text-[var(--hd-text-muted)] mt-3">
        Plan can be changed any time from Settings → Billing.
      </p>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-[var(--hd-border-strong)] bg-[var(--hd-surface-soft)]/60 px-4 py-3">
      <span className="text-[var(--hd-text-muted)]">{label}</span>
      <span className="font-mono font-semibold">{value}</span>
    </div>
  );
}

function ProfileEditor({ onSaved }) {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/users/me", { name }).catch(() => null);
      await refresh?.();
      onSaved?.();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="hd-field max-w-sm">
      <label>Display name</label>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        data-testid="onboarding-profile-name"
      />
      <button
        onClick={save}
        disabled={busy || !name.trim()}
        className="hd-btn hd-btn--secondary mt-3"
        data-testid="onboarding-profile-save"
      >
        {busy ? "Saving…" : "Save profile"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Default action wiring per step — what does "Get started" do?
// ---------------------------------------------------------------------------
function primaryActionFor(stepId, navigate, mark) {
  const go = (path) => () => navigate(path);
  switch (stepId) {
    case "welcome":          return { label: "Continue",                 onClick: null /* uses mark complete */ };
    case "confirm_plan":     return { label: "Looks good",               onClick: null };
    case "profile":          return { label: "Save & continue",          onClick: null /* in-step button */ };
    case "billing":          return { label: "Open billing",             onClick: go("/app/billing") };
    case "first_number":     return { label: "Browse numbers",           onClick: go("/app/numbers") };
    case "test_call":        return { label: "Open dialer",              onClick: () => { mark?.("test_call_made"); navigate("/app/dialer"); } };
    case "test_sms":         return { label: "Open messages",            onClick: go("/app/sms") };
    case "tour":             return { label: "Take me to dashboard",     onClick: () => { mark?.("tour_completed"); navigate("/app"); } };
    case "invite_team":      return { label: "Invite teammates",         onClick: go("/app/team") };
    case "seats_roles":      return { label: "Manage roles",             onClick: go("/app/team") };
    case "business_hours":   return { label: "Set hours",                onClick: go("/app/ivr") };
    case "ivr_routing":      return { label: "Build IVR",                onClick: go("/app/ivr") };
    case "voicemail_setup":  return { label: "Configure voicemail",      onClick: go("/app/ivr") };
    case "greetings":        return { label: "Set greetings",            onClick: go("/app/ivr") };
    case "call_recording":   return { label: "Recording preferences",    onClick: go("/app/settings") };
    case "departments":      return { label: "Set up departments",       onClick: go("/app/team") };
    case "compliance":       return { label: "Compliance settings",      onClick: go("/app/settings") };
    default:                 return { label: "Continue",                 onClick: null };
  }
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { state, steps, loading, currentStepId, progressPct, isCompleted, complete, skip, mark } = useOnboarding();
  const [busy, setBusy] = useState(false);

  // Step picked = currently active in the right pane (defaults to currentStepId).
  const [activeId, setActiveId] = useState(null);
  const visibleStep = useMemo(() => {
    const target = activeId || currentStepId;
    return steps.find((s) => s.id === target) || steps[0];
  }, [activeId, currentStepId, steps]);

  if (loading) {
    return (
      <div className="hbf-premium-scope min-h-screen flex items-center justify-center">
        <div className="hd-spinner" />
      </div>
    );
  }
  if (!user) {
    navigate("/login");
    return null;
  }
  if (isCompleted) {
    return (
      <div className="hbf-premium-scope min-h-screen flex flex-col items-center justify-center px-6" data-testid="onboarding-complete">
        <div className="hd-card hd-card--glass hd-fade-up text-center max-w-md px-10 py-12">
          <div className="hd-empty__icon !w-16 !h-16 mb-2"><CheckCircle size={34} weight="duotone" /></div>
          <h1 className="mt-3 text-[26px] font-bold tracking-tight">You're all set</h1>
          <p className="mt-2 text-[14px] text-[var(--hd-text-muted)]">
            Onboarding complete. Welcome to Hello by Flow — let's make some calls.
          </p>
          <button
            onClick={() => navigate("/app")}
            className="hd-btn hd-btn--primary mt-6"
            data-testid="onboarding-go-dashboard"
          >
            Go to dashboard <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  const done = new Set(state?.completed_steps || []);
  const skipped = new Set(state?.skipped_steps || []);
  const primary = primaryActionFor(visibleStep?.id, navigate, mark);

  const onPrimary = async () => {
    if (!visibleStep) return;
    if (primary.onClick) {
      primary.onClick();
      // most CTAs navigate away; mark complete (auto) so checklist updates on return
      setBusy(true);
      try {
        await complete(visibleStep.id);
      } catch (e) {
        // Non-fatal — the checklist will simply not auto-mark this step. The
        // user has already been navigated away by primary.onClick(); the next
        // page load will re-fetch onboarding status.
        if (process.env.NODE_ENV !== "production") console.debug("onboarding: complete() failed", e);
      }
      setBusy(false);
      return;
    }
    setBusy(true);
    try { await complete(visibleStep.id); }
    finally { setBusy(false); setActiveId(null); }
  };

  const onSkip = async () => {
    if (!visibleStep) return;
    setBusy(true);
    try { await skip(visibleStep.id); }
    finally { setBusy(false); setActiveId(null); }
  };

  return (
    <div className="hbf-premium-scope min-h-screen" data-testid="onboarding-page">
      {/* Glass header */}
      <header className="sticky top-0 z-20 border-b border-[var(--hd-border)] px-8 py-4 flex items-center justify-between backdrop-blur-md"
              style={{ background: "linear-gradient(180deg, rgba(255,255,255,.92), rgba(255,255,255,.80))" }}>
        <div className="flex items-center gap-3">
          <Logo />
          <span className="hd-hero__eyebrow">Getting started</span>
        </div>
        <button
          onClick={() => navigate("/app")}
          className="hd-btn hd-btn--ghost !py-2 !px-3.5"
          data-testid="onboarding-skip-for-now-btn"
        >
          Skip for now <X size={14} />
        </button>
      </header>

      {/* Two-column */}
      <div className="max-w-6xl mx-auto px-8 py-10 grid grid-cols-1 md:grid-cols-[300px_1fr] gap-8">
        {/* Step list — floating card */}
        <aside data-testid="onboarding-step-list" className="hd-card hd-card--static hd-fade-up p-5 self-start">
          <div className="mb-5">
            <div className="hd-panel__eyebrow !mt-0 mb-2">Progress</div>
            <div className="hd-progress">
              <div
                className="hd-progress__fill"
                style={{ width: `${progressPct}%` }}
                data-testid="onboarding-progress-bar"
              />
            </div>
            <div className="mt-2 text-[11.5px] text-[var(--hd-text-muted)] font-mono" data-testid="onboarding-progress-pct">
              {progressPct}% — {done.size} of {steps.length - skipped.size} done
            </div>
          </div>
          <ol className="space-y-1">
            {steps.map((s) => {
              const isDone = done.has(s.id);
              const isSkip = skipped.has(s.id);
              const isActive = (activeId || currentStepId) === s.id;
              return (
                <li key={s.id}>
                  <button
                    onClick={() => setActiveId(s.id)}
                    className={`w-full text-left px-3 py-2 text-[13.5px] flex items-center gap-3 rounded-xl border transition-all duration-150 ${
                      isActive
                        ? "border-[var(--hd-accent)] bg-[rgba(37,99,235,.06)] shadow-[inset_0_0_0_1px_rgba(37,99,235,.15)] font-semibold"
                        : "border-transparent hover:bg-[var(--hd-surface-soft)] hover:-translate-y-px"
                    }`}
                    data-testid={`onboarding-step-row-${s.id}`}
                  >
                    {isDone ? (
                      <CheckCircle size={18} weight="fill" className="text-emerald-500 shrink-0" />
                    ) : isSkip ? (
                      <SkipForward size={18} className="text-[var(--hd-text-subtle)] shrink-0" />
                    ) : (
                      <Circle size={18} className={`shrink-0 ${isActive ? "text-[var(--hd-accent)]" : "text-[var(--hd-text-subtle)]"}`} />
                    )}
                    <span className={`flex-1 ${isDone ? "text-[var(--hd-text-subtle)] line-through" : ""}`}>
                      {s.title}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        {/* Active step — glass card */}
        <section data-testid="onboarding-active-step" className="hd-card hd-card--glass hd-fade-up hd-stagger-1 p-8 self-start">
          {visibleStep && (
            <>
              <h1 className="text-[24px] font-bold tracking-tight" data-testid="onboarding-active-title">{visibleStep.title}</h1>
              <p className="mt-1.5 text-[13.5px] text-[var(--hd-text-muted)]">{visibleStep.desc}</p>
              <div className="mt-7" data-testid={`onboarding-step-body-${visibleStep.id}`}>
                <StepBody step={visibleStep} onPrimary={onPrimary} onSkip={onSkip} primaryBusy={busy} />
              </div>
              <div className="mt-9 pt-6 border-t border-[var(--hd-border)] flex items-center gap-3">
                <button
                  onClick={onPrimary}
                  disabled={busy}
                  className="hd-btn hd-btn--primary"
                  data-testid="onboarding-continue-btn"
                >
                  {primary.label} <ArrowRight size={14} />
                </button>
                {visibleStep.skippable && (
                  <button
                    onClick={onSkip}
                    disabled={busy}
                    className="hd-btn hd-btn--ghost"
                    data-testid="onboarding-skip-step-btn"
                  >
                    Skip this step
                  </button>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
